/**
 * Página de instalação do PWA
 * 
 * Funcionalidades:
 * - Exibe botão de instalação para Chrome/Android
 * - Mostra instruções manuais para iOS/Safari
 * - Detecta se o app já está instalado
 * - Responsivo para mobile e desktop
 */
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, Share, CheckCircle, Smartphone, Monitor, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { usePWAInstall } from '@/hooks/usePWAInstall';
import { useSystemSettings } from '@/hooks/useSystemSettings';

export default function Install() {
  const navigate = useNavigate();
  const { isInstallable, isInstalled, isIOS, promptInstall, isInstalling } = usePWAInstall();
  const { branding } = useSystemSettings();

  // Redireciona se já estiver instalado
  useEffect(() => {
    if (isInstalled) {
      // Se já está instalado, mostra mensagem de sucesso por alguns segundos
      const timeout = setTimeout(() => {
        navigate('/');
      }, 3000);
      return () => clearTimeout(timeout);
    }
  }, [isInstalled, navigate]);

  // Handler para instalar o app
  const handleInstall = async () => {
    const success = await promptInstall();
    if (success) {
      // Após instalação bem sucedida, o app será aberto automaticamente
      // ou o usuário pode acessá-lo pela home screen
    }
  };

  // Se já está instalado, mostra mensagem de sucesso
  if (isInstalled) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md text-center">
          <CardHeader>
            <div className="mx-auto mb-4 p-4 rounded-full bg-green-100 dark:bg-green-900/30">
              <CheckCircle className="h-12 w-12 text-green-600 dark:text-green-400" />
            </div>
            <CardTitle className="text-2xl">App Instalado!</CardTitle>
            <CardDescription>
              O {branding?.app_name || 'UaizyChat'} já está instalado no seu dispositivo.
              Você pode acessá-lo pela sua tela inicial.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/')} className="w-full">
              Continuar para o app
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          {/* Logo do sistema */}
          <div className="mx-auto mb-4 p-4 rounded-2xl bg-primary/10">
            {branding?.icon_url ? (
              <img 
                src={branding.icon_url} 
                alt={branding.app_name || 'UaizyChat'} 
                className="h-16 w-16 object-contain"
              />
            ) : (
              <Smartphone className="h-16 w-16 text-primary" />
            )}
          </div>
          
          <CardTitle className="text-2xl">
            Instalar {branding?.app_name || 'UaizyChat'}
          </CardTitle>
          <CardDescription>
            Instale o app no seu dispositivo para acesso rápido e experiência completa.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Benefícios */}
          <div className="space-y-3">
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <div className="p-2 rounded-lg bg-muted">
                <Smartphone className="h-4 w-4" />
              </div>
              <span>Acesso rápido pela tela inicial</span>
            </div>
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <div className="p-2 rounded-lg bg-muted">
                <Monitor className="h-4 w-4" />
              </div>
              <span>Tela cheia sem barra do navegador</span>
            </div>
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <div className="p-2 rounded-lg bg-muted">
                <Download className="h-4 w-4" />
              </div>
              <span>Carregamento mais rápido</span>
            </div>
          </div>

          {/* Botão de instalação (Chrome/Android) */}
          {isInstallable && (
            <Button 
              onClick={handleInstall} 
              className="w-full h-12 text-base"
              disabled={isInstalling}
            >
              {isInstalling ? (
                <>Instalando...</>
              ) : (
                <>
                  <Download className="mr-2 h-5 w-5" />
                  Instalar App
                </>
              )}
            </Button>
          )}

          {/* Instruções para iOS */}
          {isIOS && !isInstallable && (
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-muted/50 border border-border">
                <h4 className="font-medium mb-3 text-center">Como instalar no iPhone/iPad</h4>
                <ol className="space-y-3 text-sm text-muted-foreground">
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-medium">
                      1
                    </span>
                    <span className="flex items-center gap-2">
                      Toque em <Share className="h-4 w-4 inline" /> <strong>Compartilhar</strong>
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-medium">
                      2
                    </span>
                    <span>Role para baixo e toque em</span>
                  </li>
                  <li className="flex items-start gap-3 ml-9">
                    <span className="flex items-center gap-2 bg-muted px-3 py-1.5 rounded-lg">
                      <Plus className="h-4 w-4" />
                      <strong>Adicionar à Tela de Início</strong>
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-medium">
                      3
                    </span>
                    <span>Toque em <strong>Adicionar</strong></span>
                  </li>
                </ol>
              </div>
            </div>
          )}

          {/* Mensagem genérica se não for instalável e não for iOS */}
          {!isInstallable && !isIOS && (
            <div className="p-4 rounded-lg bg-muted/50 border border-border text-center">
              <p className="text-sm text-muted-foreground">
                Para instalar o app, acesse pelo navegador Chrome no seu celular Android 
                ou Safari no iPhone.
              </p>
            </div>
          )}

          {/* Link para voltar */}
          <div className="text-center">
            <Button variant="ghost" onClick={() => navigate(-1)}>
              Voltar
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
